/**
 * BoardFilters — utvalget på tavla: Mine / Ledige / Alle som `Chip` med
 * tellere, og personfilteret (#154, steg 4b; Stell_Oppgaver.dc.html).
 *
 * Mine og Ledige viser hvor mange kort de har (CountBadge i Chip). Alle har
 * ingen teller, som i skissen.
 *
 * Personfilteret er en knapp til høyre («Filtrer på person») som åpner raden
 * med personchipene. Raden er den samme som før: «Alle» og én chip per
 * familiemedlem. Knappen er fylt mens en person er valgt, og navnet står i
 * knappens tilgjengelige navn og på den valgte chipen, så fargen er ikke
 * eneste kjennetegn.
 *
 * Personfilteret vises bare under Ledige og Alle: under Mine ville det enten
 * gjort ingenting (seeren selv) eller gitt en tom liste (alle andre).
 */
import { useId, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Chip } from '../../components/primitives/Chip';
import { Icon } from '../../components/primitives/Icon';
import type { BoardScope } from './boardFilter';
import type { BasicMember } from './useFamilyMembers';

const SCOPES: readonly BoardScope[] = ['mine', 'open', 'all'] as const;

interface BoardFiltersProps {
  scope: BoardScope;
  onScopeChange: (scope: BoardScope) => void;
  /** Tellerne på Mine og Ledige. */
  counts: { mine: number; open: number };
  /** Selected member chip, or null for everyone. */
  memberId: string | null;
  onMemberChange: (memberId: string | null) => void;
  /** Family members offered as chips; empty hides the person filter. */
  members: BasicMember[];
}

export function BoardFilters({
  scope,
  onScopeChange,
  counts,
  memberId,
  onMemberChange,
  members,
}: BoardFiltersProps): ReactNode {
  const { t } = useTranslation();
  const whoId = useId();
  // Åpen fra start når en person allerede er valgt (f.eks. tilbake fra Mine),
  // så det aktive filteret alltid er synlig.
  const [whoOpen, setWhoOpen] = useState(memberId !== null);
  const showWho = scope !== 'mine' && members.length > 0;
  const selectedName = members.find((m) => m.id === memberId)?.short_name;
  // Kommer personfilteret tilbake (fra Mine) med en person valgt, åpnes raden
  // igjen, så det aktive filteret synes. Justert under render, ikke i en effekt.
  const [prevShowWho, setPrevShowWho] = useState(showWho);
  if (showWho !== prevShowWho) {
    setPrevShowWho(showWho);
    if (showWho && memberId !== null) setWhoOpen(true);
  }

  return (
    <div className="flex flex-col gap-2.5 pt-3.5 pb-1">
      <div
        role="group"
        aria-label={t('chores.scope.label')}
        data-testid="board-scope"
        className="flex flex-wrap items-center gap-2"
      >
        {SCOPES.map((key) => (
          <Chip
            key={key}
            selected={scope === key}
            {...(key === 'all' ? {} : { count: counts[key] })}
            data-testid={`board-scope-${key}`}
            onClick={() => onScopeChange(key)}
          >
            {t(`chores.scope.${key}`)}
          </Chip>
        ))}
        {showWho ? (
          <button
            type="button"
            // i18next-kontekst: `toggle_active` når en person er valgt.
            aria-label={t('chores.who.toggle', {
              context: selectedName ? 'active' : '',
              name: selectedName ?? '',
            })}
            aria-expanded={whoOpen}
            aria-controls={whoOpen ? whoId : undefined}
            data-testid="board-who-toggle"
            onClick={() => setWhoOpen((open) => !open)}
            className={[
              'ml-auto flex h-11 w-11 shrink-0 items-center justify-center rounded-pill transition-colors',
              memberId
                ? 'bg-solid text-on-solid'
                : 'bg-secondary text-ink border border-secondary-line hover:bg-inset',
            ].join(' ')}
          >
            <Icon.NavFamily size={20} />
          </button>
        ) : null}
      </div>

      {/* Hvem — foreldre filtrerer kjøkkenskjermen ned til ett barn; barna ser
          hva et søsken holder på med. */}
      {showWho && whoOpen ? (
        <div
          id={whoId}
          role="group"
          aria-label={t('chores.who.label')}
          data-testid="board-who"
          className="flex flex-wrap items-center gap-2"
        >
          <Chip
            selected={memberId === null}
            data-testid="board-who-all"
            onClick={() => onMemberChange(null)}
          >
            {t('chores.who.everyone')}
          </Chip>
          {members.map((m) => (
            <Chip
              key={m.id}
              selected={memberId === m.id}
              data-testid={`board-who-${m.id}`}
              onClick={() => onMemberChange(memberId === m.id ? null : m.id)}
            >
              {m.short_name}
            </Chip>
          ))}
        </div>
      ) : null}
    </div>
  );
}
